import { Dispatch, SetStateAction } from 'react';
import { BatchRowsData, BatchRowsRes, ValidationIssue } from 'src/shared/api/table/types';
import {
  getRandomInt,
  isEmptyDataObject,
  setError,
  setSuccess,
  setWarning,
} from 'src/shared/lib/utils';
import { TableSettingsForm } from 'src/shared/store/projects/types';
import { initNoticeData } from 'src/shared/store/table/constants';
import { ListItem } from 'src/shared/store/table/types';

export const loadTopScroll = () => {
  const tableContainer = document.querySelector('#table-container');

  if (tableContainer) {
    const top = tableContainer.clientHeight / 2;
    tableContainer.scrollTo({ top });
  }
};

export const scrollTableToBottom = () => {
  const tableContainer = document.querySelector('#table-container');

  if (tableContainer) {
    const top = tableContainer.scrollHeight - 100;
    tableContainer.scrollTo({ top });
  }
};

export const createEmptyRows = (length: number, tableSettings: TableSettingsForm) =>
  Array.from({ length }).map(() => createEmptyRow(tableSettings));

export const createEmptyRow = (tableSettings: TableSettingsForm | null): ListItem => {
  const newRow = {};

  tableSettings?.formStructure.forEach(({ columnName, columnType }) => {
    if (columnType === 'actions') {
      newRow[columnName] = { isEdit: false, isRemoved: false, newId: getRandomInt(9999999) };
    }
    if (columnType === 'text') {
      newRow[columnName] = '';
    }
    if (columnType === 'number' || columnType === 'floatNumber') {
      newRow[columnName] = null;
    }
    if (columnType === 'date') {
      newRow[columnName] = null;
    }
    if (columnType === 'dropdown') {
      newRow[columnName] = '';
    }
    if (columnType === 'boolean') {
      newRow[columnName] = false;
    }
  });

  return newRow;
};

type ParamsType = {
  initialData: string;
  clearlyData: ListItem[];
  readOnlyColumns?: Set<string>;
};

const normalizeColumnKey = (value: string): string => value.trim().toLowerCase();

const technicalColumns = new Set(['actions', 'isdateopen', 'isopen', 'dateopen', 'selectopen']);

const shouldSkipMutationColumn = (columnName: string, readOnlyColumns?: Set<string>): boolean => {
  const normalized = normalizeColumnKey(columnName);
  if (!normalized) {
    return true;
  }
  if (normalized === 'id') {
    return true;
  }
  if (technicalColumns.has(normalized)) {
    return true;
  }
  return Boolean(readOnlyColumns?.has(normalized));
};

const sanitizeMutationData = (
  source: ListItem | undefined,
  readOnlyColumns?: Set<string>,
): ListItem => {
  if (!source) {
    return {};
  }

  return Object.keys(source).reduce<ListItem>((acc, key) => {
    if (shouldSkipMutationColumn(key, readOnlyColumns)) {
      return acc;
    }
    const value = source[key];
    acc[key] = value;
    return acc;
  }, {});
};

const buildChangedData = (
  previous: ListItem | undefined,
  current: ListItem,
  readOnlyColumns?: Set<string>,
): ListItem => {
  const previousSanitized = sanitizeMutationData(previous, readOnlyColumns);
  const currentSanitized = sanitizeMutationData(current, readOnlyColumns);

  return Object.keys(currentSanitized).reduce<ListItem>((acc, key) => {
    const value = currentSanitized[key];
    if (JSON.stringify(value) !== JSON.stringify(previousSanitized[key])) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const createRowsBatchData = (params: ParamsType): BatchRowsData => {
  const { initialData, clearlyData, readOnlyColumns } = params;

  const data: BatchRowsData = { create: [], delete: [], update: [] };

  const initial: ListItem[] = !initialData || initialData === 'null' ? [] : JSON.parse(initialData);

  initial.forEach(item => {
    const newItem = clearlyData.find(el => el?.id === item?.id);

    if (!newItem) {
      if (typeof item.id === 'number') {
        data.delete = [...data.delete, item.id];
      }
    }

    if (newItem && JSON.stringify(newItem) !== JSON.stringify(item)) {
      if (typeof newItem.id !== 'number') {
        return;
      }
      const changedData = buildChangedData(item, newItem, readOnlyColumns);
      if (!Object.keys(changedData).length) {
        return;
      }

      data.update = [
        ...data.update,
        {
          data: changedData,
          id: newItem.id,
        },
      ];
    }
  });

  clearlyData.forEach(el => {
    if (!el.id && !isEmptyDataObject(el)) {
      const createData = sanitizeMutationData(el, readOnlyColumns);
      if (!Object.keys(createData).length) {
        return;
      }
      // Оборачиваем данные в объект `data`, чтобы соответствовать модели на сервере
      data.create = [
        ...data.create,
        {
          data: createData,
        },
      ];
    }
  });

  return data;
};

type ParamsResType = {
  initialData: string;
  data: BatchRowsRes;
  setTableData: Dispatch<SetStateAction<ListItem[]>>;
  setInitialData: Dispatch<SetStateAction<string>>;
};

export const parseRowsBatchDataRes = (params: ParamsResType) => {
  const { initialData, data, setInitialData, setTableData } = params;

  const { createResults, deleteResults, updateResults, workflowWarnings } = data;
  const errors = { ...initNoticeData };
  const success = { ...initNoticeData };
  const ruleWarnings: string[] = [];
  const flkWarnings: ValidationIssue[] = [];
  const flkErrors: ValidationIssue[] = [];
  let init: ListItem[] = !initialData || initialData === 'null' ? [] : JSON.parse(initialData);

  const classifyValidationIssues = (issues?: ValidationIssue[]) => {
    if (!issues?.length) {
      return;
    }
    issues.forEach(issue => {
      if ((issue?.level || '').toLowerCase() === 'warning') {
        flkWarnings.push(issue);
      } else {
        flkErrors.push(issue);
      }
    });
  };

  if (deleteResults.length) {
    deleteResults.forEach(res => {
      if (res.success) {
        success.delete += 1;
        init = init.filter(el => el.id !== res.data.id);
        if (res.warnings?.length) {
          ruleWarnings.push(...res.warnings.filter(Boolean));
        }
        classifyValidationIssues(res.validationIssues);
      } else {
        errors.delete += 1;
        classifyValidationIssues(res.validationIssues);
      }
    });
  }

  if (createResults.length) {
    createResults.forEach(res => {
      if (res.success) {
        success.create += 1;
        init.push(res.data);
        if (res.warnings?.length) {
          ruleWarnings.push(...res.warnings.filter(Boolean));
        }
        classifyValidationIssues(res.validationIssues);
      } else {
        errors.create += 1;
        classifyValidationIssues(res.validationIssues);
      }
    });
  }

  if (updateResults.length) {
    updateResults.forEach(res => {
      if (res.success) {
        success.update += 1;
        init = init.map(el => (el.id === res.data.id ? res.data : el));
        if (res.warnings?.length) {
          ruleWarnings.push(...res.warnings.filter(Boolean));
        }
        classifyValidationIssues(res.validationIssues);
      } else {
        errors.update += 1;
        classifyValidationIssues(res.validationIssues);
      }
    });
  }

  const list = init.reduce((filtered: ListItem[], item: ListItem) => {
    if (Object.keys(item)?.length > 1) {
      filtered.push({
        ...item,
        actions: { isEdit: false, isRemoved: false },
      });
    }
    return filtered;
  }, []);

  setTableData(list);
  setInitialData(JSON.stringify(init));

  if (errors.update || errors.create || errors.delete) {
    setError(
      `Ошибка при ${errors.delete ? `удалении(${errors.delete})` : ''} ${errors.create ? `создании(${errors.create})` : ''} ${errors.update ? `изменении(${errors.update})` : ''}`,
    );
  }
  if (flkErrors.length) {
    const uniqueMessages = Array.from(
      new Set(
        flkErrors
          .map(issue => {
            const row = issue.rowId ? `${issue.rowId}` : '';
            const column = issue.column || '';
            const location = row && column ? `[${row}:${column}] ` : row ? `[${row}] ` : '';
            return `${location}${issue.message}`;
          })
          .filter(Boolean),
      ),
    );
    if (uniqueMessages.length) {
      const preview = uniqueMessages.slice(0, 3).join('; ');
      const extraCount = uniqueMessages.length - Math.min(uniqueMessages.length, 3);
      const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
      setError(`Ошибки ФЛК: ${preview}${suffix}`);
    }
  }
  if (success.update || success.create || success.delete) {
    setSuccess(
      `Успешно ${success.delete ? `удалено(${success.delete})` : ''} ${success.create ? `создано(${success.create})` : ''} ${success.update ? `изменено(${success.update})` : ''}`,
    );
  }
  if (ruleWarnings.length) {
    const uniqueWarnings = Array.from(new Set(ruleWarnings));
    const preview = uniqueWarnings.slice(0, 3).join('; ');
    const extraCount = uniqueWarnings.length - Math.min(uniqueWarnings.length, 3);
    const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
    setWarning(`Предупреждения бизнес-правил: ${preview}${suffix}`);
  }
  if (flkWarnings.length) {
    const uniqueMessages = Array.from(
      new Set(
        flkWarnings
          .map(issue => {
            const row = issue.rowId ? `${issue.rowId}` : '';
            const column = issue.column || '';
            const location = row && column ? `[${row}:${column}] ` : row ? `[${row}] ` : '';
            return `${location}${issue.message}`;
          })
          .filter(Boolean),
      ),
    );
    if (uniqueMessages.length) {
      const preview = uniqueMessages.slice(0, 3).join('; ');
      const extraCount = uniqueMessages.length - Math.min(uniqueMessages.length, 3);
      const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
      setWarning(`Предупреждения ФЛК: ${preview}${suffix}`);
    }
  }
  if (workflowWarnings?.length) {
    const uniqueWarnings = Array.from(new Set(workflowWarnings.filter(Boolean)));
    const preview = uniqueWarnings.slice(0, 3).join('; ');
    const extraCount = uniqueWarnings.length - Math.min(uniqueWarnings.length, 3);
    const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
    setWarning(`Бизнес-процесс: ${preview}${suffix}`);
  }
};
